import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  RefreshCw,
  Eye,
  FileText,
  CreditCard,
  User,
  History,
  Coins,
  Receipt,
  Info,
  HelpCircle,
  Banknote
} from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { agrupacionesAPI, describirError } from '../../services/api';
import { formatoMoneda, limpiarNombreGrupo, limpiarNombreProducto } from '../../utils/finanzas';
import '../../styles/Pages.css';
import '../../styles/DataTable.css';
import '../../styles/Agrupaciones.css';

const MESES_ABR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const fechaCorta = (iso) => {
  if (!iso || iso === '—' || iso === '-') return '—';
  if (typeof iso === 'string') {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = match[1];
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = match[3];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${MESES_ABR[monthIdx]} ${year}`;
      }
    }
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

export default function AgrupacionExpediente({ tipo: propTipo }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reviewMode } = useAuth();

  const [agrupacion, setAgrupacion] = useState(null);
  const [activeTab, setActiveTab] = useState('pago-periodo');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros rápidos de cartera activa
  const [filtroCartera, setFiltroCartera] = useState('CON_SALDO'); // 'CON_SALDO' | 'TODOS'
  const [filtroIntegrantes, setFiltroIntegrantes] = useState('CON_SALDO'); // 'CON_SALDO' | 'TODOS'
  const [filtroCreditosTab, setFiltroCreditosTab] = useState('CON_SALDO'); // 'CON_SALDO' | 'TODOS'
  const [cicloFrecuencia, setCicloFrecuencia] = useState(null); // 'SEMANAL' | 'QUINCENAL'

  // Tab sub-data states
  const [pagoPeriodoData, setPagoPeriodoData] = useState(null);
  const [creditos, setCreditos] = useState([]);
  const [integrantes, setIntegrantes] = useState([]);
  const [cobranza, setCobranza] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);

  const cargarDetalle = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resAgrupacion, resPago] = await Promise.all([
        agrupacionesAPI.obtener(id),
        agrupacionesAPI.pagoPeriodo(id, cicloFrecuencia ? { frecuencia: cicloFrecuencia } : {})
      ]);
      setAgrupacion(resAgrupacion.data);
      setPagoPeriodoData(resPago.data.data);
      if (resPago.data.data?.frecuenciaSeleccionada && !cicloFrecuencia) {
        setCicloFrecuencia(resPago.data.data.frecuenciaSeleccionada);
      }
    } catch (err) {
      setError(describirError(err).mensaje);
    } finally {
      setLoading(false);
    }
  }, [id, cicloFrecuencia]);

  useEffect(() => {
    cargarDetalle();
  }, [cargarDetalle]);

  // Cargar datos según tab activa o cambio de frecuencia
  useEffect(() => {
    if (!agrupacion) return;

    async function cargarTab() {
      setTabLoading(true);
      try {
        if (activeTab === 'pago-periodo') {
          const res = await agrupacionesAPI.pagoPeriodo(id, cicloFrecuencia ? { frecuencia: cicloFrecuencia } : {});
          setPagoPeriodoData(res.data.data);
        } else if (activeTab === 'creditos') {
          const res = await agrupacionesAPI.creditos(id);
          setCreditos(res.data.data || []);
        } else if (activeTab === 'integrantes') {
          const res = await agrupacionesAPI.integrantes(id);
          setIntegrantes(res.data.data || []);
        } else if (activeTab === 'cobranza') {
          const res = await agrupacionesAPI.cobranza(id);
          setCobranza(res.data.data || []);
        } else if (activeTab === 'pagos') {
          const res = await agrupacionesAPI.pagos(id);
          setPagos(res.data.data || []);
        } else if (activeTab === 'auditoria') {
          const res = await agrupacionesAPI.historial(id);
          setAuditLog(res.data.data || []);
        }
      } catch (err) {
        console.error('Error cargando tab:', err);
      } finally {
        setTabLoading(false);
      }
    }

    cargarTab();
  }, [id, agrupacion, activeTab, cicloFrecuencia]);

  const isColocadora = agrupacion?.tipoAgrupacion === 'COLOCADORA' || propTipo === 'COLOCADORA';
  const backPath = isColocadora ? '/colocadoras' : '/grupos';

  if (loading && !agrupacion) {
    return (
      <div className="cartera-container">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <RefreshCw size={24} className="spin text-primary" style={{ margin: '0 auto 1rem' }} />
          <p className="text-muted">Cargando expediente de la agrupación...</p>
        </div>
      </div>
    );
  }

  if (error || !agrupacion) {
    return (
      <div className="cartera-container">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(backPath)}
          style={{ marginBottom: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <ArrowLeft size={14} />
          <span>Volver</span>
        </button>
        <div className="cartera-error-card">
          <AlertTriangle size={18} />
          <span>{error || 'No fue posible cargar la agrupación solicitada.'}</span>
        </div>
      </div>
    );
  }

  const pMetricas = pagoPeriodoData?.metricas || {};
  const pClientes = pagoPeriodoData?.clientes || [];
  const maxAtrasoDias = Number(agrupacion.kpis?.maxDiasAtraso || pMetricas.periodo?.diasAtraso || 0);

  const parseMonto = (val) => {
    if (typeof val === 'number') return val;
    return parseFloat(String(val || 0).replace(/[^0-9.-]/g, '')) || 0;
  };

  // Cartera Activa vs Todos
  const creditosConSaldoList = pClientes.filter(c => parseMonto(c.saldoActual) > 0.001);
  const conSaldoCarteraCount = creditosConSaldoList.length;
  const totalCarteraCount = pClientes.length;
  const displayedCartera = filtroCartera === 'CON_SALDO' ? creditosConSaldoList : pClientes;

  // Orden default sugerido:
  // 1. créditos vencidos (estadoPago === 'VENCIDO' o diasAtraso > 0)
  // 2. mayor días atraso
  // 3. próximo vencimiento
  // 4. mayor saldo
  const sortedCartera = [...displayedCartera].sort((a, b) => {
    const aVenc = a.estadoPago === 'VENCIDO' || (a.diasAtraso > 0) ? 1 : 0;
    const bVenc = b.estadoPago === 'VENCIDO' || (b.diasAtraso > 0) ? 1 : 0;
    if (bVenc !== aVenc) return bVenc - aVenc;
    if ((b.diasAtraso || 0) !== (a.diasAtraso || 0)) return (b.diasAtraso || 0) - (a.diasAtraso || 0);
    return parseMonto(b.saldoActual) - parseMonto(a.saldoActual);
  });

  const handleCambiarFrecuencia = async (nuevaFrecuencia) => {
    if (cicloFrecuencia === nuevaFrecuencia) return;
    setCicloFrecuencia(nuevaFrecuencia);
    setTabLoading(true);
    try {
      const res = await agrupacionesAPI.pagoPeriodo(id, { frecuencia: nuevaFrecuencia });
      setPagoPeriodoData(res.data.data);
    } catch (err) {
      console.error('Error cambiando ciclo de frecuencia:', err);
    } finally {
      setTabLoading(false);
    }
  };

  return (
    <div className="cartera-container">
      {/* Botón Volver */}
      <div style={{ marginBottom: '0.25rem' }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(backPath)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <ArrowLeft size={14} />
          <span>Volver a {isColocadora ? 'Colocadoras' : 'Grupos'}</span>
        </button>
      </div>

      {/* Alerta de Cartera de 91+ Días para Grupos: ÚNICAMENTE si efectivamente presenta 91+ días de atraso */}
      {!isColocadora && maxAtrasoDias >= 91 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: '0.5rem',
          marginBottom: '0.5rem',
          color: '#92400e',
          fontSize: '0.875rem'
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <div>
            <strong>CARTERA CON ANTIGÜEDAD DE 91+ DÍAS:</strong> Existen créditos del grupo con saldo pendiente y antigüedad igual o mayor a 91 días.
          </div>
        </div>
      )}

      {/* Header del Expediente - Entidad Padre con Máxima Jerarquía */}
      <div className="cartera-header" style={{ marginBottom: '1.25rem', backgroundColor: '#ffffff', padding: '1.25rem 1.5rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div>
          {(() => {
            const { nombre: nombreLimpio, badge: badgeNombre } = limpiarNombreGrupo(agrupacion.nombre);
            return (
              <h1 className="cartera-title" style={{ fontSize: '1.85rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span>{nombreLimpio}</span>
                {badgeNombre && (
                  <span style={{
                    fontSize: '0.75rem',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '0.375rem',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    fontWeight: '600',
                    border: '1px solid #e2e8f0',
                    whiteSpace: 'nowrap'
                  }}>
                    {badgeNombre}
                  </span>
                )}
              </h1>
            );
          })()}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <span className="font-mono" style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95rem' }}>
              {agrupacion.folio}
            </span>
            <span style={{ color: '#cbd5e1' }}>•</span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              backgroundColor: isColocadora ? '#ede9fe' : '#e0e7ff',
              color: isColocadora ? '#6b21a8' : '#3730a3',
              fontSize: '0.75rem',
              fontWeight: '700',
              padding: '0.15rem 0.55rem',
              borderRadius: '0.375rem',
              letterSpacing: '0.05em'
            }}>
              {isColocadora ? 'COLOCADORA' : 'GRUPO OPERATIVO'}
            </span>
            <span style={{ color: '#cbd5e1' }}>•</span>
            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569' }}>
              {isColocadora 
                ? `${agrupacion.kpis?.creditosConSaldo ?? conSaldoCarteraCount} créditos con saldo • ${agrupacion.kpis?.integrantesConSaldo ?? (pMetricas.clientCount || 0)} clientes con saldo` 
                : `${agrupacion.kpis?.totalIntegrantes || 0} integrantes`}
            </span>
            <span style={{ color: '#cbd5e1' }}>•</span>
            <StatusBadge value={agrupacion.estado} />
            {reviewMode && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#0369a1', backgroundColor: '#e0f2fe', padding: '0.15rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600', marginLeft: 'auto' }}>
                MODO REVISIÓN · READ_ONLY
              </span>
            )}
          </div>

          {agrupacion.responsable && (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', fontSize: '0.875rem', color: '#64748b', flexWrap: 'wrap', marginTop: '0.25rem' }}>
              <span>Responsable: <strong>{agrupacion.responsable.nombre}</strong> ({agrupacion.responsable.folio})</span>
              {agrupacion.responsable.telefono && <span>Tel: {agrupacion.responsable.telefono}</span>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cargarDetalle}
            title="Actualizar expediente"
          >
            <RefreshCw size={15} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* BLOQUE HERO PRINCIPAL DE PAGO */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#1e293b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Banknote size={15} className="text-primary" />
            <span>{isColocadora ? 'Hero de Pago y Liquidación' : 'Resumen Operativo del Periodo'}</span>
          </div>

          {/* SELECTOR DISCRETO DE FRECUENCIA PARA COLOCADORAS MIXTAS */}
          {isColocadora && pagoPeriodoData?.esMixta && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Ciclo de Liquidación:
              </span>
              <div style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#f1f5f9', padding: '0.2rem', borderRadius: '0.375rem', border: '1px solid #cbd5e1' }}>
                <button
                  type="button"
                  onClick={() => handleCambiarFrecuencia('SEMANAL')}
                  style={{
                    padding: '0.25rem 0.75rem',
                    fontSize: '0.75rem',
                    fontWeight: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'SEMANAL' ? '700' : '500',
                    color: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'SEMANAL' ? '#1e293b' : '#64748b',
                    backgroundColor: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'SEMANAL' ? '#ffffff' : 'transparent',
                    border: 'none',
                    borderRadius: '0.25rem',
                    boxShadow: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'SEMANAL' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                    cursor: 'pointer'
                  }}
                >
                  SEMANAL
                </button>
                <button
                  type="button"
                  onClick={() => handleCambiarFrecuencia('QUINCENAL')}
                  style={{
                    padding: '0.25rem 0.75rem',
                    fontSize: '0.75rem',
                    fontWeight: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'QUINCENAL' ? '700' : '500',
                    color: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'QUINCENAL' ? '#1e293b' : '#64748b',
                    backgroundColor: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'QUINCENAL' ? '#ffffff' : 'transparent',
                    border: 'none',
                    borderRadius: '0.25rem',
                    boxShadow: (cicloFrecuencia || pagoPeriodoData?.frecuenciaSeleccionada) === 'QUINCENAL' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                    cursor: 'pointer'
                  }}
                >
                  QUINCENAL
                </button>
              </div>
            </div>
          )}
        </div>

        {/* HERO COLOCADORA: PAGO BRUTO -> COMISIÓN -> TOTAL A PAGAR */}
        {isColocadora ? (
          <>
            <div className="cartera-kpi-grid" style={{ marginBottom: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
              {/* PAGO DEL CICLO */}
              <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #2563eb', backgroundColor: '#ffffff', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="cartera-kpi-label" style={{ color: '#1d4ed8', fontWeight: '800', fontSize: '0.85rem' }}>
                  PAGO DEL CICLO
                </div>
                <div className="cartera-kpi-value font-mono text-primary" style={{ fontSize: 'clamp(1.5rem, 2vw, 1.85rem)', fontWeight: '800', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                  {formatoMoneda(pMetricas.grossPeriodPayment || pMetricas.pagoBrutoPeriodo)}
                </div>
                <div className="cartera-kpi-sub" style={{ color: '#3b82f6', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  Suma de cuotas de cobro de los clientes
                </div>
              </div>

              {/* COMISIÓN */}
              <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #d97706', backgroundColor: '#ffffff', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="cartera-kpi-label" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#b45309', fontWeight: '800', fontSize: '0.85rem' }}>
                  <span>MENOS COMISIÓN {pMetricas.commissionRatePercent ? `(${pMetricas.commissionRatePercent})` : '(0%)'}</span>
                  <span title={pMetricas.comision?.tooltip || 'Regla contractual de comisión'} style={{ cursor: 'help' }}>
                    <HelpCircle size={14} style={{ color: '#d97706' }} />
                  </span>
                </div>
                <div className="cartera-kpi-value font-mono" style={{ fontSize: 'clamp(1.5rem, 2vw, 1.85rem)', color: '#b45309', fontWeight: '800', minHeight: '2.4rem', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                  {pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                    ? '$0.00'
                    : pMetricas.commissionStatus === 'CALCULATED'
                      ? (Number(pMetricas.commissionAmount || 0) > 0 ? `-${formatoMoneda(pMetricas.commissionAmount)}` : '$0.00')
                      : (pMetricas.comision?.texto || 'Pendiente de regla')}
                </div>
                <div className="cartera-kpi-sub" style={{ color: '#d97706', fontSize: '0.75rem', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  {pMetricas.comision?.descripcion || (pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                    ? 'Sin clientes con pago exigible en este periodo'
                    : pMetricas.commissionStatus === 'CALCULATED'
                      ? (pMetricas.commissionRate === 0
                          ? 'Menos de 5 clientes: sin comisión'
                          : `Rango: ${pMetricas.commissionRange ? pMetricas.commissionRange.replace('_', '–') : ''} clientes (${pMetricas.commissionRatePercent} sobre ${formatoMoneda(pMetricas.grossPeriodPayment)})`)
                      : 'Comisión pendiente de regla')}
                </div>
              </div>

              {/* TOTAL A PAGAR (MÁXIMA JERARQUÍA VISUAL) */}
              <div className="cartera-kpi-card" style={{
                borderLeft: '6px solid #059669',
                backgroundColor: '#f0fdf4',
                padding: '1.25rem',
                border: '2px solid #a7f3d0',
                borderLeftWidth: '6px',
                boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.1), 0 2px 4px -1px rgba(16, 185, 129, 0.06)'
              }}>
                <div className="cartera-kpi-label" style={{ color: '#065f46', fontWeight: '800', fontSize: '0.9rem', letterSpacing: '0.025em' }}>
                  TOTAL A PAGAR
                </div>
                <div className="cartera-kpi-value font-mono" style={{ fontSize: 'clamp(1.8rem, 2.4vw, 2.2rem)', color: '#047857', fontWeight: '900', minHeight: '2.4rem', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                  {pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                    ? '$0.00'
                    : formatoMoneda(pMetricas.netPayment || pMetricas.totalNeto?.valor || pMetricas.totalNeto?.texto)}
                </div>
                <div className="cartera-kpi-sub" style={{ color: '#059669', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                    ? 'Sin clientes con pago exigible en este periodo'
                    : pMetricas.commissionStatus === 'CALCULATED'
                      ? 'Total neto a entregar a la colocadora'
                      : 'Requiere comisión calculada'}
                </div>
              </div>
            </div>

            {/* Tarjetas Secundarias de Cartera para Colocadora con Diferenciación Histórica / Colocados / Con Saldo */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '0.75rem',
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem'
            }}>
              <div>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                  Saldo Total Cartera
                </div>
                <div className="font-mono font-bold" style={{ fontSize: '1.1rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                  {formatoMoneda(pMetricas.saldoTotalCartera || agrupacion.kpis?.saldoActual)}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Total saldo vivo</div>
              </div>

              <div style={{ borderLeft: '1px solid #f1f5f9', paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                  Saldo Vencido
                </div>
                <div className="font-mono font-bold text-danger" style={{ fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
                  {formatoMoneda(pMetricas.saldoVencido || agrupacion.kpis?.saldoVencido)}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#ef4444' }}>Exigible no cubierto</div>
              </div>

              <div style={{ borderLeft: '1px solid #f1f5f9', paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                  Saldo Por Vencer
                </div>
                <div className="font-mono font-bold text-success" style={{ fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
                  {formatoMoneda(pMetricas.saldoPorVencer || agrupacion.kpis?.saldoPorVencer)}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#10b981' }}>Cuotas futuras sanas</div>
              </div>

              <div style={{ borderLeft: '1px solid #f1f5f9', paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                  Vencimiento de Cobro
                </div>
                <div className="font-mono font-bold" style={{ fontSize: '1rem', color: '#334155', whiteSpace: 'nowrap' }}>
                  {pMetricas.periodo?.vencimiento ? fechaCorta(pMetricas.periodo.vencimiento) : '—'}
                </div>
                <div style={{ fontSize: '0.7rem', color: (pMetricas.periodo?.diasAtraso || 0) > 0 ? '#ef4444' : '#10b981' }}>
                  {pMetricas.periodo?.diasAtraso || 0} d atraso
                </div>
              </div>

              <div style={{ borderLeft: '1px solid #f1f5f9', paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                  Créditos Históricos
                </div>
                <div className="font-mono font-bold" style={{ fontSize: '1.1rem', color: '#0f172a', whiteSpace: 'nowrap' }}>
                  {agrupacion.kpis?.creditosHistoricos || agrupacion.metadataMigracion?.historicalCreditCount || agrupacion.kpis?.creditosVinculados || 0}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                  Colocados: <strong>{agrupacion.kpis?.creditosColocados || agrupacion.kpis?.creditosVinculados || totalCarteraCount}</strong>
                </div>
              </div>

              <div style={{ borderLeft: '1px solid #f1f5f9', paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                  Créditos Con Saldo
                </div>
                <div className="font-mono font-bold text-primary" style={{ fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
                  {agrupacion.kpis?.creditosConSaldo ?? conSaldoCarteraCount}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#3b82f6' }}>
                  Clientes con saldo: <strong>{agrupacion.kpis?.integrantesConSaldo ?? (pMetricas.clientCount || 0)}</strong>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* HERO GRUPO: PAGO DEL PERIODO, SALDO TOTAL, SALDO VENCIDO, SALDO POR VENCER, VENCIMIENTO, ATRASO */
          <div className="cartera-kpi-grid">
            {/* PAGO DEL PERIODO */}
            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #2563eb', backgroundColor: '#eff6ff', padding: '1.125rem 1.25rem', boxShadow: '0 1px 3px rgba(37,99,235,0.08)' }}>
              <div className="cartera-kpi-label" style={{ color: '#1d4ed8', fontWeight: '800', fontSize: '0.8rem' }}>
                PAGO DEL PERIODO
              </div>
              <div className="cartera-kpi-value font-mono text-primary" style={{ fontSize: 'clamp(1.45rem, 1.8vw, 1.85rem)', fontWeight: '800', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                {formatoMoneda(pMetricas.grossPeriodPayment || pMetricas.pagoBrutoPeriodo)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#3b82f6', fontWeight: '600', whiteSpace: 'nowrap' }}>
                {pMetricas.totalClientes || agrupacion.kpis?.totalIntegrantes || 0} integrantes · {pMetricas.totalCreditos || agrupacion.kpis?.creditosVinculados || 0} créditos
              </div>
            </div>

            {/* SALDO TOTAL */}
            <div className="cartera-kpi-card" style={{ padding: '1.125rem 1.25rem' }}>
              <div className="cartera-kpi-label">SALDO TOTAL</div>
              <div className="cartera-kpi-value font-mono" style={{ fontSize: 'clamp(1.25rem, 1.5vw, 1.6rem)', whiteSpace: 'nowrap' }}>
                {formatoMoneda(pMetricas.saldoTotalCartera || agrupacion.kpis?.saldoActual)}
              </div>
              <div className="cartera-kpi-sub" style={{ whiteSpace: 'nowrap' }}>Total saldo vivo</div>
            </div>

            {/* SALDO VENCIDO */}
            <div className="cartera-kpi-card" style={{ padding: '1.125rem 1.25rem' }}>
              <div className="cartera-kpi-label">SALDO VENCIDO</div>
              <div className="cartera-kpi-value font-mono text-danger" style={{ fontSize: 'clamp(1.25rem, 1.5vw, 1.6rem)', whiteSpace: 'nowrap' }}>
                {formatoMoneda(pMetricas.saldoVencido || agrupacion.kpis?.saldoVencido)}
              </div>
              <div className="cartera-kpi-sub text-danger" style={{ color: '#ef4444', whiteSpace: 'nowrap' }}>Exigible acumulado</div>
            </div>

            {/* SALDO POR VENCER */}
            <div className="cartera-kpi-card" style={{ padding: '1.125rem 1.25rem' }}>
              <div className="cartera-kpi-label">SALDO POR VENCER</div>
              <div className="cartera-kpi-value font-mono text-success" style={{ fontSize: 'clamp(1.25rem, 1.5vw, 1.6rem)', whiteSpace: 'nowrap' }}>
                {formatoMoneda(pMetricas.saldoPorVencer || agrupacion.kpis?.saldoPorVencer)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#10b981', whiteSpace: 'nowrap' }}>Cuotas futuras sanas</div>
            </div>

            {/* VENCIMIENTO */}
            <div className="cartera-kpi-card" style={{ padding: '1.125rem 1.25rem' }}>
              <div className="cartera-kpi-label">VENCIMIENTO</div>
              <div className="cartera-kpi-value font-mono" style={{ fontSize: 'clamp(1.15rem, 1.4vw, 1.45rem)', color: '#334155', whiteSpace: 'nowrap' }}>
                {pMetricas.periodo?.vencimiento ? fechaCorta(pMetricas.periodo.vencimiento) : '—'}
              </div>
              <div className="cartera-kpi-sub" style={{ whiteSpace: 'nowrap' }}>Vencimiento del periodo</div>
            </div>

            {/* ATRASO */}
            <div className="cartera-kpi-card" style={{ padding: '1.125rem 1.25rem' }}>
              <div className="cartera-kpi-label">ATRASO</div>
              <div className="cartera-kpi-value font-mono" style={{ fontSize: 'clamp(1.25rem, 1.5vw, 1.5rem)', color: (pMetricas.periodo?.diasAtraso || 0) > 0 ? '#dc2626' : '#059669', whiteSpace: 'nowrap' }}>
                {pMetricas.periodo?.diasAtraso ? `${pMetricas.periodo.diasAtraso} días` : '0 días'}
              </div>
              <div className="cartera-kpi-sub" style={{ color: (pMetricas.periodo?.diasAtraso || 0) > 0 ? '#ef4444' : '#10b981', whiteSpace: 'nowrap' }}>
                {(pMetricas.periodo?.diasAtraso || 0) > 0 ? 'Con atraso en periodo' : 'Al corriente'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navegación por Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '1.25rem',
        overflowX: 'auto'
      }}>
        {[
          { key: 'pago-periodo', label: isColocadora ? 'Pago del Periodo' : 'Pago del Grupo', icon: Banknote, highlight: true },
          { key: 'resumen', label: 'Resumen Entidad', icon: FileText },
          { key: 'creditos', label: `Créditos (${agrupacion.kpis?.creditosVinculados || 0})`, icon: CreditCard },
          { key: 'integrantes', label: isColocadora ? `Clientes (${agrupacion.kpis?.totalIntegrantes || 0})` : `Integrantes (${agrupacion.kpis?.totalIntegrantes || 0})`, icon: User },
          { key: 'cobranza', label: 'Cobranza', icon: Coins },
          { key: 'pagos', label: 'Historial Pagos', icon: Receipt },
          { key: 'auditoria', label: 'Auditoría', icon: History }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.65rem 1rem',
                border: 'none',
                background: 'none',
                borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                color: isActive ? '#2563eb' : (tab.highlight ? '#1e293b' : '#64748b'),
                fontWeight: isActive ? '600' : '500',
                fontSize: '0.875rem',
                cursor: 'pointer',
                marginBottom: '-2px',
                transition: 'all 0.15s ease'
              }}
            >
              <Icon size={15} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Contenido de Tabs */}
      {tabLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <RefreshCw size={20} className="spin text-primary" style={{ margin: '0 auto 0.5rem' }} />
          <p className="text-muted text-sm">Cargando información...</p>
        </div>
      ) : (
        <>
          {/* TAB: PAGO DEL PERIODO (OPERATIVO PRINCIPAL) */}
          {activeTab === 'pago-periodo' && (
            <div>
              {/* Sección Subentidades: CARTERA DE LA COLOCADORA / CARTERA DEL GRUPO */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', margin: '0 0 0.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <User size={18} className="text-primary" />
                    <span>{isColocadora ? 'CARTERA DE LA COLOCADORA' : 'CARTERA DEL GRUPO'}</span>
                  </h3>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {filtroCartera === 'CON_SALDO'
                      ? `${conSaldoCarteraCount} créditos con saldo`
                      : `${totalCarteraCount} créditos colocados`}
                  </span>
                </div>

                {/* Filtro Rápido de Cartera: CON SALDO vs TODOS LOS CRÉDITOS */}
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${filtroCartera === 'CON_SALDO' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFiltroCartera('CON_SALDO')}
                    style={{ fontWeight: '600', fontSize: '0.8rem' }}
                  >
                    CON SALDO ({conSaldoCarteraCount})
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${filtroCartera === 'TODOS' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFiltroCartera('TODOS')}
                    style={{ fontWeight: '600', fontSize: '0.8rem' }}
                  >
                    TODOS LOS CRÉDITOS ({totalCarteraCount})
                  </button>
                </div>
              </div>

              {/* Tabla de Desglose de Cartera */}
              <div className="table-responsive" style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{isColocadora ? 'CLIENTE' : 'INTEGRANTE'}</th>
                      <th>FOLIO CRÉDITO</th>
                      <th>PRODUCTO</th>
                      <th className="text-right" style={{ color: '#1d4ed8', backgroundColor: '#eff6ff' }}>{isColocadora ? 'PAGO DEL CICLO' : 'PAGO DEL PERIODO'}</th>
                      <th className="text-center">{isColocadora ? 'VENCIMIENTO DE COBRO' : 'VENCIMIENTO'}</th>
                      <th className="text-right">SALDO ACTUAL</th>
                      <th className="text-right">SALDO VENCIDO</th>
                      <th className="text-right">SALDO POR VENCER</th>
                      <th className="text-center">DÍAS ATRASO</th>
                      <th>ESTADO</th>
                      <th className="text-right">ACCIÓN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCartera.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center py-4 text-muted">
                          {filtroCartera === 'CON_SALDO'
                            ? 'No hay créditos con saldo activo en esta agrupación.'
                            : 'No hay créditos vinculados a esta agrupación.'}
                        </td>
                      </tr>
                    ) : (
                      sortedCartera.map(c => (
                        <tr
                          key={c.creditoId}
                          className="fila-clicable"
                          onClick={() => navigate(`/creditos/${c.creditoId}`)}
                          title={`Ver crédito ${c.creditoFolio}`}
                        >
                          <td>
                            <div className="font-semibold text-slate-800">{c.clienteNombre}</div>
                            <div className="text-xs text-muted font-mono">{c.clienteFolio}</div>
                          </td>
                          <td>
                            <span className="font-mono text-bold creditos-folio-cell">{c.creditoFolio}</span>
                          </td>
                          <td>
                            <span className="text-xs font-semibold text-slate-600">{limpiarNombreProducto(c.productoNombre)}</span>
                          </td>
                          <td className="text-right font-mono font-bold text-primary" style={{ backgroundColor: '#f8fafc', fontSize: '0.95rem' }}>
                            {formatoMoneda(c.pagoPeriodo)}
                          </td>
                          <td className="text-center font-mono text-sm">
                            {fechaCorta(c.vencimiento)}
                          </td>
                          <td className="text-right font-mono font-semibold">
                            {formatoMoneda(c.saldoActual)}
                          </td>
                          <td className="text-right font-mono font-semibold text-danger">
                            {formatoMoneda(c.saldoVencido)}
                          </td>
                          <td className="text-right font-mono text-muted">
                            {formatoMoneda(c.saldoPorVencer)}
                          </td>
                          <td className="text-center font-mono">
                            {c.diasAtraso > 0 ? (
                              <span className="badge badge-danger">{c.diasAtraso} d</span>
                            ) : (
                              <span className="badge badge-success">0 d</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${c.estadoPago === 'VENCIDO' ? 'badge-danger' : c.estadoPago === 'EXIGIBLE' ? 'badge-warning' : 'badge-success'}`}>
                              {c.estadoPago}
                            </span>
                          </td>
                          <td className="text-right" onClick={e => e.stopPropagation()}>
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon"
                              onClick={() => navigate(`/creditos/${c.creditoId}`)}
                              title="Ver detalle del crédito"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {sortedCartera.length > 0 && (
                    <tfoot>
                      <tr style={{ backgroundColor: '#f8fafc', fontWeight: '700', borderTop: '2px solid #cbd5e1' }}>
                        <td colSpan="3" className="text-right uppercase">{isColocadora ? 'TOTAL PAGO DEL CICLO:' : 'TOTAL PAGO INTEGRANTES:'}</td>
                        <td className="text-right font-mono text-primary font-bold" style={{ fontSize: '1.05rem', backgroundColor: '#eff6ff' }}>
                          {formatoMoneda(pMetricas.grossPeriodPayment || pMetricas.pagoBrutoPeriodo)}
                        </td>
                        <td className="text-center font-mono text-xs text-muted">
                          {pMetricas.periodo?.vencimiento ? fechaCorta(pMetricas.periodo.vencimiento) : '—'}
                        </td>
                        <td className="text-right font-mono">
                          {formatoMoneda(pMetricas.saldoTotalCartera)}
                        </td>
                        <td className="text-right font-mono text-danger">
                          {formatoMoneda(pMetricas.saldoVencido)}
                        </td>
                        <td className="text-right font-mono text-muted">
                          {formatoMoneda(pMetricas.saldoPorVencer)}
                        </td>
                        <td className="text-center font-mono">
                          {pMetricas.periodo?.diasAtraso} d máx
                        </td>
                        <td colSpan="2" className="text-muted text-xs">
                          {sortedCartera.length} {isColocadora ? 'créditos visibles' : 'integrantes'}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* BLOQUE DE CIERRE DEL PAGO: Exclusivo para Colocadoras */}
              {isColocadora && (
                /* CIERRE COLOCADORA: Cobro - Comisión = Neto */
                <>
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.75rem',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                  }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Receipt size={17} className="text-primary" />
                      <span>Cierre de Liquidación del Periodo</span>
                    </h4>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: '1rem',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.5rem',
                      padding: '1.25rem'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>
                          1. TOTAL PAGO DEL CICLO
                        </div>
                        <div className="font-mono font-bold text-primary" style={{ fontSize: '1.5rem', marginTop: '0.2rem' }}>
                          {formatoMoneda(pMetricas.grossPeriodPayment || pMetricas.pagoBrutoPeriodo)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Suma de cuotas exigibles</div>
                      </div>

                      <div style={{ borderLeft: '1px solid #cbd5e1', paddingLeft: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#b45309', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <span>2. MENOS COMISIÓN {pMetricas.commissionRatePercent ? `(${pMetricas.commissionRatePercent})` : '(0%)'}</span>
                          <HelpCircle size={13} style={{ color: '#d97706' }} />
                        </div>
                        <div className="font-mono" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#b45309', marginTop: '0.2rem' }}>
                          {pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                            ? '$0.00'
                            : pMetricas.commissionStatus === 'CALCULATED'
                              ? (Number(pMetricas.commissionAmount || 0) > 0 ? `-${formatoMoneda(pMetricas.commissionAmount)}` : '$0.00')
                              : (pMetricas.comision?.texto || 'Pendiente de regla')}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#b45309' }}>
                          {pMetricas.comision?.descripcion || (pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                            ? 'Sin clientes con pago exigible en este periodo'
                            : pMetricas.commissionStatus === 'CALCULATED'
                              ? (pMetricas.commissionRate === 0
                                  ? 'Menos de 5 clientes: sin comisión'
                                  : `${pMetricas.commissionRatePercent} sobre ${formatoMoneda(pMetricas.grossPeriodPayment)}`)
                              : 'Sin regla contractual configurada')}
                        </div>
                      </div>

                      <div style={{ borderLeft: '1px solid #cbd5e1', paddingLeft: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#047857', fontWeight: '700' }}>
                          3. TOTAL A PAGAR COLOCADORA
                        </div>
                        <div className="font-mono" style={{ fontSize: '1.5rem', fontWeight: '800', color: '#047857', marginTop: '0.2rem' }}>
                          {pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                            ? '$0.00'
                            : formatoMoneda(pMetricas.netPayment || pMetricas.totalNeto?.valor || pMetricas.totalNeto?.texto)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                          {pMetricas.commissionStatus === 'NO_PERIOD_ACTIVITY'
                            ? 'Sin clientes con pago exigible en este periodo'
                            : pMetricas.commissionStatus === 'CALCULATED'
                              ? 'Neto a liquidar = Pago del Ciclo - Comisión'
                              : 'Sujeto a regla de comisión'}
                        </div>
                      </div>
                    </div>

                    <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.85rem', marginBottom: 0 }}>
                      Nota: El pago del ciclo corresponde a la suma de las cuotas seleccionadas para cobro de los clientes. El saldo vencido refleja el total vencido acumulado de la cartera.
                    </p>
                  </div>

                  {/* Sección Informativa: Tabulador Vigente de Comisiones */}
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem',
                    padding: '1.25rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '1.25rem'
                  }}>
                    <div>
                      <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Info size={16} className="text-primary" />
                        <span>Marco Normativo de Comisiones</span>
                      </h4>
                      <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>
                        Política vigente {pMetricas.commissionPolicyVersion || 'V1_2026_TIERED_CLIENT_COUNT'}: Se calcula comisión escalonada sobre el pago bruto exigible del periodo según el número de clientes comisionables únicos (0: sin actividad, 1-4: 0%, 5-10: 12%, 11-20: 13%, 21-30: 15%, 31-35: 16%). A partir de 36 clientes permanece pendiente de confirmación formal.
                      </p>
                    </div>

                    <div style={{ backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '0.375rem', padding: '0.875rem' }}>
                      <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '600', marginBottom: '0.35rem' }}>
                        Tabulador Vigente de Colocadoras
                      </div>
                      <ul style={{ fontSize: '0.8rem', color: '#475569', margin: 0, paddingLeft: '1.2rem', lineHeight: '1.5' }}>
                        <li><strong>0 clientes:</strong> Sin actividad exigible del periodo</li>
                        <li><strong>1 – 4 clientes:</strong> 0% comisión</li>
                        <li><strong>5 – 10 clientes:</strong> 12% comisión</li>
                        <li><strong>11 – 20 clientes:</strong> 13% comisión</li>
                        <li><strong>21 – 30 clientes:</strong> 15% comisión</li>
                        <li><strong>31 – 35 clientes:</strong> 16% comisión</li>
                        <li><strong>36+ clientes:</strong> Pendiente de regla</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB: RESUMEN ENTIDAD */}
          {activeTab === 'resumen' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
              <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.25rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#1e293b' }}>
                  Ficha de la Entidad
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', rowGap: '0.75rem', fontSize: '0.875rem' }}>
                  <span className="text-muted">Código:</span>
                  <span className="font-mono font-semibold">{agrupacion.folio}</span>

                  <span className="text-muted">Nombre:</span>
                  {(() => {
                    const { nombre: nombreLimpio, badge: badgeNombre } = limpiarNombreGrupo(agrupacion.nombre);
                    return (
                      <span className="font-semibold" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <span>{nombreLimpio}</span>
                        {badgeNombre && (
                          <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '0.25rem', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', border: '1px solid #e2e8f0' }}>
                            {badgeNombre}
                          </span>
                        )}
                      </span>
                    );
                  })()}

                  <span className="text-muted">Tipo:</span>
                  <span>{agrupacion.tipoAgrupacion}</span>

                  <span className="text-muted">Estado:</span>
                  <div><StatusBadge value={agrupacion.estado} /></div>

                  <span className="text-muted">Fecha Creación:</span>
                  <span>{agrupacion.createdAt ? new Date(agrupacion.createdAt).toLocaleDateString('es-MX') : '—'}</span>
                </div>
              </div>

              <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.25rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#1e293b' }}>
                  Responsable / Titular Asignado
                </h3>
                {agrupacion.responsable ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', rowGap: '0.75rem', fontSize: '0.875rem' }}>
                    <span className="text-muted">Nombre:</span>
                    <span className="font-semibold">{agrupacion.responsable.nombre}</span>

                    <span className="text-muted">Folio Cliente:</span>
                    <span className="font-mono">{agrupacion.responsable.folio || '—'}</span>

                    <span className="text-muted">Teléfono:</span>
                    <span>{agrupacion.responsable.telefono || '—'}</span>

                    <span className="text-muted">RFC:</span>
                    <span className="font-mono">{agrupacion.responsable.rfc || '—'}</span>
                  </div>
                ) : (
                  <div className="text-muted text-sm py-3">
                    No tiene un responsable directo asignado en el catálogo.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB: CRÉDITOS */}
          {activeTab === 'creditos' && (() => {
            const conSaldoCreditosList = creditos.filter(c => parseMonto(c.saldoActual) > 0.001);
            const displayedCreditos = filtroCreditosTab === 'CON_SALDO' ? conSaldoCreditosList : creditos;
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', margin: '0 0 0.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <CreditCard size={18} className="text-primary" />
                      <span>{isColocadora ? 'CRÉDITOS DE LA COLOCADORA' : 'CRÉDITOS DEL GRUPO'}</span>
                    </h3>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {filtroCreditosTab === 'CON_SALDO'
                        ? `${conSaldoCreditosList.length} créditos con saldo`
                        : `${creditos.length} créditos colocados`}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${filtroCreditosTab === 'CON_SALDO' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setFiltroCreditosTab('CON_SALDO')}
                      style={{ fontWeight: '600', fontSize: '0.8rem' }}
                    >
                      CON SALDO ({conSaldoCreditosList.length})
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${filtroCreditosTab === 'TODOS' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setFiltroCreditosTab('TODOS')}
                      style={{ fontWeight: '600', fontSize: '0.8rem' }}
                    >
                      TODOS LOS CRÉDITOS ({creditos.length})
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>NÚMERO DE CRÉDITO</th>
                        <th>PRODUCTO / TIPO</th>
                        <th>CLIENTE</th>
                        <th className="text-center">INICIO</th>
                        <th className="text-center">TÉRMINO</th>
                        <th className="text-right">SALDO ACTUAL</th>
                        <th className="text-right">SALDO VENCIDO</th>
                        <th className="text-right">SALDO POR VENCER</th>
                        <th>ESTATUS</th>
                        <th className="text-right">ACCIÓN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedCreditos.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center py-4 text-muted">
                            {filtroCreditosTab === 'CON_SALDO'
                              ? 'No hay créditos con saldo activo en esta agrupación.'
                              : 'No hay créditos vinculados a esta agrupación.'}
                          </td>
                        </tr>
                      ) : (
                        displayedCreditos.map(c => (
                          <tr
                            key={c.id}
                            className="fila-clicable"
                            onClick={() => navigate(`/creditos/${c.id}`)}
                            title={`Ver crédito ${c.folio}`}
                          >
                            <td><span className="font-mono text-bold creditos-folio-cell">{c.folio}</span></td>
                            <td>
                              <div>
                                <span className="text-sm font-semibold">{limpiarNombreProducto(c.producto?.nombre || c.tipoCredito)}</span>
                                <div className="text-xs text-muted">{c.frecuencia || c.tipoCredito}</div>
                              </div>
                            </td>
                            <td>
                              <div className="font-semibold">{c.titular?.nombreCompleto || '—'}</div>
                            </td>
                            <td className="text-center font-mono text-sm">
                              {fechaCorta(c.fechaDesembolso || c.metadataMigracion?.inicio || c.metadataMigracion?.fechaDesembolso)}
                            </td>
                            <td className="text-center font-mono text-sm">
                              {fechaCorta(c.fechaFinContractual || c.metadataMigracion?.vence || c.metadataMigracion?.fechaVence)}
                            </td>
                            <td className="text-right font-mono font-semibold">{formatoMoneda(c.saldoActual)}</td>
                            <td className="text-right font-mono font-semibold text-danger">{formatoMoneda(c.saldoVencidoReal)}</td>
                            <td className="text-right font-mono text-muted">{formatoMoneda(c.saldoPorVencer)}</td>
                            <td><StatusBadge value={c.estado} /></td>
                            <td className="text-right" onClick={e => e.stopPropagation()}>
                              <button
                                type="button"
                                className="btn btn-ghost btn-icon"
                                onClick={() => navigate(`/creditos/${c.id}`)}
                                title="Ver crédito"
                              >
                                <Eye size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* TAB: INTEGRANTES / CLIENTES */}
          {activeTab === 'integrantes' && (() => {
            const conSaldoIntegrantesList = integrantes.filter(i => parseMonto(i.saldoActual) > 0.001);
            const displayedIntegrantes = filtroIntegrantes === 'CON_SALDO' ? conSaldoIntegrantesList : integrantes;
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', margin: '0 0 0.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <User size={18} className="text-primary" />
                      <span>{isColocadora ? 'CLIENTES ÚNICOS DE LA COLOCADORA' : 'INTEGRANTES ÚNICOS DEL GRUPO'}</span>
                    </h3>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {filtroIntegrantes === 'CON_SALDO'
                        ? `${conSaldoIntegrantesList.length} clientes únicos con saldo`
                        : `${integrantes.length} clientes únicos registrados`}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${filtroIntegrantes === 'CON_SALDO' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setFiltroIntegrantes('CON_SALDO')}
                      style={{ fontWeight: '600', fontSize: '0.8rem' }}
                    >
                      CON SALDO ({conSaldoIntegrantesList.length})
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${filtroIntegrantes === 'TODOS' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setFiltroIntegrantes('TODOS')}
                      style={{ fontWeight: '600', fontSize: '0.8rem' }}
                    >
                      TODOS LOS CLIENTES ({integrantes.length})
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>FOLIO</th>
                        <th>CLIENTE</th>
                        <th>RFC</th>
                        <th>TELÉFONO</th>
                        <th className="text-center">CRÉDITOS</th>
                        <th className="text-right">SALDO ACTUAL</th>
                        <th className="text-right">SALDO VENCIDO</th>
                        <th>ESTADO</th>
                        <th className="text-right">ACCIÓN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedIntegrantes.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4 text-muted">
                            {filtroIntegrantes === 'CON_SALDO'
                              ? 'No hay clientes con saldo activo en esta agrupación.'
                              : 'No hay integrantes registrados en esta agrupación.'}
                          </td>
                        </tr>
                      ) : (
                        displayedIntegrantes.map(integ => (
                          <tr
                            key={integ.id}
                            className="fila-clicable"
                            onClick={() => navigate(`/clientes/${integ.id}`)}
                            title={`Ver expediente de ${integ.nombreCompleto}`}
                          >
                            <td><span className="font-mono text-bold creditos-folio-cell">{integ.folio}</span></td>
                            <td><span className="font-semibold">{integ.nombreCompleto}</span></td>
                            <td><span className="font-mono text-sm">{integ.rfc || '—'}</span></td>
                            <td><span className="text-sm">{integ.telefono || '—'}</span></td>
                            <td className="text-center font-mono">{integ.creditosEnGrupo}</td>
                            <td className="text-right font-mono font-semibold">{formatoMoneda(integ.saldoActual)}</td>
                            <td className="text-right font-mono font-semibold text-danger">{formatoMoneda(integ.saldoVencido)}</td>
                            <td><StatusBadge value={integ.estado} /></td>
                            <td className="text-right" onClick={e => e.stopPropagation()}>
                              <button
                                type="button"
                                className="btn btn-ghost btn-icon"
                                onClick={() => navigate(`/clientes/${integ.id}`)}
                                title="Ver expediente del cliente"
                              >
                                <Eye size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* TAB: COBRANZA */}
          {activeTab === 'cobranza' && (
            <div style={{ maxWidth: '700px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#1e293b' }}>
                Distribución de Cartera por Antigüedad
              </h3>
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>TRAMO DE ATRASO</th>
                      <th className="text-center">CRÉDITOS</th>
                      <th className="text-right">SALDO ACTUAL</th>
                      <th className="text-right">SALDO VENCIDO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cobranza.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-muted">
                          Todos los créditos de esta agrupación se encuentran liquidados.
                        </td>
                      </tr>
                    ) : (
                      cobranza.map(c => (
                        <tr key={c.tramo}>
                          <td><strong>{c.tramo}</strong></td>
                          <td className="text-center font-mono">{c.creditos}</td>
                          <td className="text-right font-mono font-semibold">{formatoMoneda(c.saldoActual)}</td>
                          <td className="text-right font-mono font-semibold text-danger">{formatoMoneda(c.saldoVencido)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB: HISTORIAL PAGOS */}
          {activeTab === 'pagos' && (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>FOLIO PAGO</th>
                    <th>FECHA</th>
                    <th>CLIENTE</th>
                    <th>CRÉDITO</th>
                    <th className="text-right">IMPORTE</th>
                    <th>FORMA PAGO</th>
                    <th>REFERENCIA</th>
                    <th>ESTADO</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        No hay pagos registrados para los créditos de esta agrupación.
                      </td>
                    </tr>
                  ) : (
                    pagos.map(p => (
                      <tr key={p.id}>
                        <td><span className="font-mono font-semibold">{p.folioPago}</span></td>
                        <td>{p.fechaPago ? fechaCorta(p.fechaPago) : '—'}</td>
                        <td>{p.clienteNombre || '—'}</td>
                        <td><span className="font-mono">{p.creditoFolio}</span></td>
                        <td className="text-right font-mono font-semibold text-success">{formatoMoneda(p.importeRecibido)}</td>
                        <td>{p.formaPago}</td>
                        <td className="font-mono text-xs">{p.referencia || '—'}</td>
                        <td><StatusBadge value={p.estado} /></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: AUDITORÍA */}
          {activeTab === 'auditoria' && (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>FECHA</th>
                    <th>ACCIÓN</th>
                    <th>USUARIO</th>
                    <th>DETALLES</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">
                        Sin eventos de auditoría registrados.
                      </td>
                    </tr>
                  ) : (
                    auditLog.map(log => (
                      <tr key={log.id}>
                        <td className="text-xs text-muted">
                          {log.createdAt ? new Date(log.createdAt).toLocaleString('es-MX') : '—'}
                        </td>
                        <td><span className="badge badge-info">{log.action}</span></td>
                        <td>{log.performedBy || 'Sistema'}</td>
                        <td className="text-xs font-mono">
                          {JSON.stringify(log.newData || log.previousData || {})}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
